import PopUpHeader from "../common/PopUp/PopUpHeader";
import BlackBg from "../common/PopUp/BlackBg";
import FixedText from "../common/PopUp/FixedText";
import ButtonGroup from "../common/PopUp/ButtonGroup";

import { useState } from "react";
import { fetchWithToken } from "../../functions/fetch";
import { onChange } from "../../functions/input";
import { updateItem } from "../../functions/local-update";
import { addSearchedField, isRenter } from "../../functions/user";
import { DateTime } from "luxon";
import { Button, Input, Textarea, Flex, useDisclosure } from "@chakra-ui/react";
import LinkLineIdPopUp from "./LinkLineIdPopUp";

const UserPopUp = ({ user, setShowPopUp, setUsers, users, setRefetchUser }) => {
  const itemName = "會員";
  const phoneRegexTW = /^[\d()+-]+$/;

  // input state
  const [name, setName] = useState(user.name);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? "");
  const [idNumber, setIdNumber] = useState(user.idNumber ?? "");
  const [address, setAddress] = useState(user.address ?? "");
  const [note, setNote] = useState(user.note ?? "");
  const [contactPersonName, setContactPersonName] = useState(
    user.contactPersonName ?? "",
  );
  const [contactPersonPhoneNumber, setContactPersonPhoneNumber] = useState(
    user.contactPersonPhoneNumber ?? "",
  );
  const [profession, setProfession] = useState(user.profession ?? "");
  const [errors, setErrors] = useState([]); // [{msg:'str'}]
  const { isOpen, onOpen, onClose } = useDisclosure();

  // fetch data

  const cancel = () => {
    setShowPopUp(false);
  };

  const getRentDate = () => {
    if (user?.rentStartDate && user?.rentEndDate) {
      return `${DateTime.fromISO(user?.rentStartDate).toFormat("yyyy/MM/dd")}  -  ${DateTime.fromISO(user?.rentEndDate).toFormat("yyyy/MM/dd")}`;
    } else {
      return "無";
    }
  };

  const updateUser = async () => {
    const formBody = {
      userId: user._id,
      name,
      phoneNumber,
      idNumber: idNumber === "" ? null : idNumber,
      address,
      note,
      contactPersonName,
      contactPersonPhoneNumber,
      profession,
    };

    const res = await fetchWithToken(
      "PUT",
      `${process.env.REACT_APP_BACKEND_HOST}/api/user/${user.lineId}`,
      formBody,
    );

    if (res.status === 400) {
      if (res.errors) {
        setErrors(res.errors);
      } else if (res.msg) {
        setErrors([{ msg: res.msg }]);
      } else {
        console.error(res);
      }
    } else if (res.status === 200) {
      setShowPopUp(false);
      setUsers((prev) => {
        const updatedUser = user;

        for (const key in formBody) {
          if (Object.hasOwnProperty.call(formBody, key)) {
            updatedUser[key] = formBody[key];
          }
        }
        // check if it's new renter with new date
        updatedUser.isRenter = isRenter(updatedUser);
        const updated = updateItem(prev, updatedUser);

        addSearchedField(updated);

        return updated;
      });
    } else {
      console.log(res);
    }
  };

  const showLinkLineIdPopUp = () => {
    onOpen();
  };

  return (
    <>
      <LinkLineIdPopUp
        isOpen={isOpen}
        onClose={onClose}
        user={user}
        users={users}
        setRefetchUser={setRefetchUser}
      />
      <BlackBg cancel={cancel} />
      <div className="absolute m-auto inset-0 w-fit h-fit bg-white z-10">
        <PopUpHeader itemName={itemName} />
        <div className="font-chinese grid grid-cols-[repeat(2,min-content_1fr)] gap-y-2 gap-x-2 p-4 whitespace-nowrap items-center">
          <FixedText
            label="會員編號："
            text={user.memberNumber}
            textColSpan="col-span-1"
          />

          <div className="flex col-span-2 justify-self-end items-center gap-x-2">
            <input
              id="isRenter"
              className="w-4 h-4"
              type="checkbox"
              checked={user.isRenter}
              disabled
            />
            <label htmlFor="isRenter">住戶</label>
          </div>

          <FixedText
            label="Line 帳號："
            text={user.hasLineId ? "已綁定" : "尚未綁定"}
            textColSpan="col-span-2"
          />
          <Flex alignItems={"end"} flexDirection={"column"}>
            {user.hasLineId ? (
              <Button colorScheme="gray" isDisabled>
                已綁定
              </Button>
            ) : (
              <Button colorScheme="red" onClick={showLinkLineIdPopUp}>
                開始綁定
              </Button>
            )}
          </Flex>

          <label className="justify-self-end">姓名 </label>
          <Input
            type="text"
            variant="filled"
            value={name}
            onChange={(event) =>
              onChange(event, setName, true, null, "姓名", errors, setErrors)
            }
          />

          <label className="justify-self-end">聯絡電話 </label>
          <Input
            type="tel"
            variant="filled"
            value={phoneNumber}
            onChange={(event) =>
              onChange(
                event,
                setPhoneNumber,
                true,
                phoneRegexTW,
                "聯絡電話",
                errors,
                setErrors,
              )
            }
          />

          <label className="justify-self-end">身分證 </label>
          <Input
            variant="filled"
            value={idNumber}
            onChange={(event) =>
              onChange(
                event,
                setIdNumber,
                false,
                null,
                "身分證",
                errors,
                setErrors,
              )
            }
          />

          <p className="justify-self-end">入住房號 </p>
          <p>{user?.roomNumber ?? "無"}</p>

          <label className="justify-self-end">租期 </label>
          <div className="flex col-span-3 gap-x-2">
            <p className="whitespace-pre">{getRentDate()}</p>
          </div>

          <label className="justify-self-end">戶籍地址</label>
          <Input
            variant="filled"
            className="col-span-3"
            value={address}
            onChange={(event) => onChange(event, setAddress)}
          />

          <label className="justify-self-end">緊急聯絡人姓名</label>
          <Input
            variant="filled"
            value={contactPersonName}
            onChange={(event) => onChange(event, setContactPersonName)}
          />

          <label className="justify-self-end">緊急聯絡人電話</label>
          <Input
            variant="filled"
            type="tel"
            value={contactPersonPhoneNumber}
            onChange={(event) => onChange(event, setContactPersonPhoneNumber)}
          />

          <label className="justify-self-end">職業</label>
          <Input
            variant="filled"
            className="col-span-3"
            value={profession}
            onChange={(event) => onChange(event, setProfession)}
          />

          <label className="justify-self-end">備註</label>
          <Textarea
            variant="filled"
            className="col-span-3"
            value={note}
            onChange={(event) => onChange(event, setNote)}
          />
          <ButtonGroup
            errors={errors}
            canDelete={false}
            cancelFunction={cancel}
            updateFunction={updateUser}
          />
        </div>
      </div>
    </>
  );
};

export default UserPopUp;
